import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type UseDebouncedAutosaveOptions<T> = Readonly<{
  save: (value: T) => Promise<false | undefined>;
  delayMs?: number;
}>;

/**
 * Debounces edits into calls to `save`, one request at a time. An edit made while a
 * save is in flight waits for it, so an older write can never land after a newer one.
 * A pending edit is saved on unmount rather than dropped. A `save` that returns `false`
 * skipped the write, so the status stays 'saving'.
 */
export const useDebouncedAutosave = <T>({
  save,
  delayMs = 800,
}: UseDebouncedAutosaveOptions<T>) => {
  const [status, setStatus] = useState<AutosaveStatus>('idle');

  const saveRef = useRef(save);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ value: T } | null>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  saveRef.current = save;

  const updateStatus = useCallback((next: AutosaveStatus) => {
    if (mountedRef.current) setStatus(next);
  }, []);

  const run = useCallback(async () => {
    if (inFlightRef.current || !pendingRef.current) return;

    const { value } = pendingRef.current;

    pendingRef.current = null;
    inFlightRef.current = true;

    try {
      const result = await saveRef.current(value);

      if (result !== false && !pendingRef.current) updateStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      updateStatus('error');
    } finally {
      inFlightRef.current = false;
    }

    if (pendingRef.current && !timerRef.current) void run();
  }, [updateStatus]);

  const schedule = useCallback(
    (value: T) => {
      pendingRef.current = { value };
      updateStatus('saving');

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void run();
      }, delayMs);
    },
    [delayMs, run, updateStatus],
  );

  const flush = useCallback(() => {
    if (!timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;
    void run();
  }, [run]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      flush();
    };
  }, [flush]);

  return { status, schedule, flush };
};
